import React, { useState } from 'react';
import { Calculator, Home, Building2, ShieldCheck, Bell, Flame, Plus, Minus, ArrowRight, Info, Smartphone, MapPin, Search, CheckCircle, AlertCircle, X, Camera, Percent } from 'lucide-react';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Input } from './ui/input';

import { BACKEND_URL } from '../config/api';
const API = `${BACKEND_URL}/api`;

const QuoteCalculator = ({ onRequestQuote }) => {
  // City verification
  const [citySearch, setCitySearch] = useState('');
  const [checking, setChecking] = useState(false);
  const [cityResult, setCityResult] = useState(null);
  
  // Property type (particulier only)
  const [propertyType, setPropertyType] = useState('house');
  
  // Motion detector type
  const [motionType, setMotionType] = useState('basic');

  // Equipment quantities
  const [equipment, setEquipment] = useState({
    hub: 1,
    motionDetectors: 2,
    doorSensors: 3,
    indoorSiren: 1,
    outdoorSiren: 0,
    smokeDetector: 0,
    keypad: 1,
    remotes: 2,
  });

  // Options
  const [options, setOptions] = useState({
    installation: true,
    programming: false,
    subscription4G: false,
    subscription4GType: 'monthly',
    wantsCameras: false,
  });

  // TVA option
  const [confirmed2Years, setConfirmed2Years] = useState(false);

  // Pricing - REAL PRICES from shop (TTC with 20% TVA)
  const pricesTTC = {
    hub: 485.76,              // Hub 2 (4G) Jeweller
    motionBasic: 81.11,       // MotionProtect Jeweller
    motionCam: 185.04,        // MotionCam HDR Jeweller
    doorSensors: 47.79,       // DoorProtect Jeweller
    indoorSiren: 94.63,       // HomeSiren Jeweller
    outdoorSiren: 180.27,     // StreetSiren Jeweller
    smokeDetector: 126.05,    // FireProtect Jeweller
    keypad: 136.38,           // KeyPad Jeweller
    remotes: 35.15,           // SpaceControl Jeweller
    programming: 150,         // Programmation seule (service, TVA 20%)
    subscription4GMonthly: 8.90,  // TVA 20% (télécom)
    subscription4GAnnual: 99.0,   // TVA 20% (télécom)
  };

  // Convert TTC to HT (prices include 20% TVA)
  const toHT = (priceTTC) => priceTTC / 1.20;

  const propertyTypes = [
    { id: 'apartment', label: 'Appartement', icon: Building2, recommended: { motionDetectors: 1, doorSensors: 2 } },
    { id: 'house', label: 'Maison', icon: Home, recommended: { motionDetectors: 3, doorSensors: 4 } },
  ];

  const equipmentList = [
    { id: 'hub', label: 'Centrale Hub 2 4G', icon: Smartphone, min: 1, max: 1, required: true, description: 'Cerveau du système' },
    { id: 'motionDetectors', label: motionType === 'basic' ? 'Détecteurs de mouvement' : 'MotionCam HDR', icon: ShieldCheck, min: 0, max: 10, description: motionType === 'basic' ? 'MotionProtect' : 'Avec photo' },
    { id: 'doorSensors', label: 'Détecteurs d\'ouverture', icon: ShieldCheck, min: 0, max: 15, description: 'DoorProtect' },
    { id: 'indoorSiren', label: 'Sirène intérieure', icon: Bell, min: 0, max: 3, description: 'HomeSiren' },
    { id: 'outdoorSiren', label: 'Sirène extérieure', icon: Bell, min: 0, max: 2, description: 'StreetSiren' },
    { id: 'smokeDetector', label: 'Détecteur de fumée', icon: Flame, min: 0, max: 5, description: 'FireProtect' },
    { id: 'keypad', label: 'Clavier à code', icon: ShieldCheck, min: 0, max: 3, description: 'KeyPad' },
    { id: 'remotes', label: 'Télécommandes', icon: ShieldCheck, min: 0, max: 6, description: 'SpaceControl' },
  ];

  // Check city availability
  const checkCity = async (e) => {
    e.preventDefault();
    if (!citySearch.trim()) return;
    
    setChecking(true);
    setCityResult(null);
    
    try {
      const response = await fetch(`${API}/services/calculate-distance`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ city: citySearch.trim() })
      });
      
      if (response.ok) {
        const data = await response.json();
        setCityResult(data);
        const type = propertyTypes.find(t => t.id === propertyType);
        if (type) {
          setEquipment(prev => ({ ...prev, ...type.recommended }));
        }
      }
    } catch (error) {
      console.error('Error checking city:', error);
    } finally {
      setChecking(false);
    }
  };

  const resetCity = () => {
    setCityResult(null);
    setCitySearch('');
    setConfirmed2Years(false);
  };

  const updateQuantity = (id, delta) => {
    const item = equipmentList.find(e => e.id === id);
    if (!item) return;
    setEquipment(prev => ({
      ...prev,
      [id]: Math.max(item.min, Math.min(item.max, prev[id] + delta))
    }));
  };

  const handlePropertyChange = (typeId) => {
    setPropertyType(typeId);
    const type = propertyTypes.find(t => t.id === typeId);
    if (type) {
      setEquipment(prev => ({ ...prev, ...type.recommended }));
    }
  };

  const getMotionPrice = () => motionType === 'basic' ? pricesTTC.motionBasic : pricesTTC.motionCam;

  // Determine TVA rate: 10% if installation + confirmed 2 years, otherwise 20%
  const tvaRate = (options.installation && confirmed2Years) ? 0.10 : 0.20;
  const tvaLabel = tvaRate === 0.10 ? '10%' : '20%';

  // Calculate totals
  const calculateTotal = () => {
    // Equipment HT (convert from TTC 20% to HT)
    let equipmentHT = 0;
    equipmentHT += equipment.hub * toHT(pricesTTC.hub);
    equipmentHT += equipment.motionDetectors * toHT(getMotionPrice());
    equipmentHT += equipment.doorSensors * toHT(pricesTTC.doorSensors);
    equipmentHT += equipment.indoorSiren * toHT(pricesTTC.indoorSiren);
    equipmentHT += equipment.outdoorSiren * toHT(pricesTTC.outdoorSiren);
    equipmentHT += equipment.smokeDetector * toHT(pricesTTC.smokeDetector);
    equipmentHT += equipment.keypad * toHT(pricesTTC.keypad);
    equipmentHT += equipment.remotes * toHT(pricesTTC.remotes);

    // Services HT
    let servicesHT = 0;
    let servicesHTwith20TVA = 0; // Services always at 20% (4G telecom)
    
    if (options.installation && cityResult?.installation_price) {
      // Installation is a service, follows the main TVA rate
      servicesHT += cityResult.installation_price / 1.20; // Installation price is TTC 20%
    }
    if (options.programming && !options.installation) {
      servicesHT += pricesTTC.programming / 1.20;
    }

    // 4G subscription always at 20% TVA (telecom service)
    let monthlyHT = 0;
    let annualHT = 0;
    if (options.subscription4G) {
      if (options.subscription4GType === 'annual') {
        annualHT = pricesTTC.subscription4GAnnual / 1.20;
        servicesHTwith20TVA += annualHT;
      } else {
        monthlyHT = pricesTTC.subscription4GMonthly / 1.20;
      }
    }

    // Calculate TVA
    const tvaEquipmentAndServices = (equipmentHT + servicesHT) * tvaRate;
    const tva4G = servicesHTwith20TVA * 0.20; // 4G always 20%
    const totalTVA = tvaEquipmentAndServices + tva4G;

    // Total TTC
    const totalHT = equipmentHT + servicesHT + servicesHTwith20TVA;
    const totalTTC = totalHT + totalTVA;

    return {
      equipmentHT,
      servicesHT,
      totalHT,
      tva: totalTVA,
      tvaRate,
      totalTTC,
      monthlyHT,
      monthlyTTC: monthlyHT * 1.20, // 4G monthly always 20%
    };
  };

  const totals = calculateTotal();

  const handleRequestQuote = () => {
    const quoteData = {
      city: cityResult?.city,
      distance: cityResult?.distance_km,
      propertyType,
      motionType,
      equipment,
      options,
      confirmed2Years,
      installationPrice: cityResult?.installation_price,
      totals,
      tvaRate,
    };
    if (onRequestQuote) {
      onRequestQuote(quoteData);
    }
  };

  return (
    <div className="bg-white/[0.02] border border-white/5 rounded-2xl overflow-hidden" data-testid="quote-calculator">
      {/* Header */}
      <div className="bg-gradient-to-r from-fgs-primary/20 to-emerald-500/10 p-6 border-b border-white/5">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-emerald-500/10 flex items-center justify-center">
            <Calculator className="w-6 h-6 text-emerald-400" />
          </div>
          <div>
            <h3 className="text-2xl font-semibold text-white">Simulateur de devis</h3>
            <p className="text-white/50 text-sm">Estimez le coût de votre système Ajax</p>
          </div>
        </div>
      </div>

      <div className="p-6 space-y-6">
        {/* Step 1: City Verification */}
        {!cityResult ? (
          <div className="space-y-4">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-7 h-7 rounded-full bg-emerald-500 flex items-center justify-center text-white text-sm font-bold">1</div>
              <Label className="text-white font-semibold">Votre ville</Label>
            </div>
            
            <form onSubmit={checkCity} className="flex gap-3">
              <div className="relative flex-1">
                <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/50" />
                <Input
                  value={citySearch}
                  onChange={(e) => setCitySearch(e.target.value)}
                  placeholder="Entrez votre ville..."
                  className="pl-10 bg-black/40 border-white/5 text-white placeholder:text-white/50 h-12"
                  data-testid="city-input"
                />
              </div>
              <Button 
                type="submit"
                disabled={checking || !citySearch.trim()}
                className="bg-emerald-500 hover:bg-emerald-500-hover h-12 px-6"
                data-testid="check-city-btn"
              >
                {checking ? (
                  <span className="flex items-center gap-2">
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Vérification
                  </span>
                ) : (
                  <>
                    <Search className="w-4 h-4 mr-2" />
                    Vérifier
                  </>
                )}
              </Button>
            </form>

            <p className="text-white/50 text-sm">
              Entrez votre ville pour vérifier la disponibilité de l'installation et obtenir le tarif exact.
            </p>
          </div>
        ) : (
          <>
            {/* City Result */}
            <div className={`p-4 rounded-xl border ${
              cityResult.installation_available 
                ? 'bg-fgs-success/10 border-fgs-success/30' 
                : 'bg-orange-500/10 border-orange-500/30'
            }`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  {cityResult.installation_available ? (
                    <CheckCircle className="w-6 h-6 text-fgs-success" />
                  ) : (
                    <AlertCircle className="w-6 h-6 text-orange-400" />
                  )}
                  <div>
                    <p className="text-white font-semibold">{cityResult.city}</p>
                    <p className="text-white/50 text-sm">
                      {cityResult.distance_km > 0 && `${cityResult.distance_km} km de Montpellier`}
                      {cityResult.installation_available && cityResult.installation_price && (
                        <span className="text-fgs-success ml-2">• Installation : {cityResult.installation_price}€ TTC</span>
                      )}
                    </p>
                  </div>
                </div>
                <button onClick={resetCity} className="text-white/50 hover:text-white p-2">
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            {cityResult.installation_available ? (
              <>
                {/* Step 2: Property Type */}
                <div>
                  <div className="flex items-center gap-2 mb-4">
                    <div className="w-7 h-7 rounded-full bg-emerald-500 flex items-center justify-center text-white text-sm font-bold">2</div>
                    <Label className="text-white font-semibold">Type de logement</Label>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-3">
                    {propertyTypes.map((type) => (
                      <button
                        key={type.id}
                        onClick={() => handlePropertyChange(type.id)}
                        className={`p-4 rounded-xl border transition-all ${
                          propertyType === type.id
                            ? 'bg-emerald-500/10 border-fgs-primary text-white'
                            : 'bg-black/40 border-white/5 text-white/50 hover:border-fgs-primary/50'
                        }`}
                      >
                        <type.icon className={`w-6 h-6 mx-auto mb-2 ${propertyType === type.id ? 'text-emerald-400' : ''}`} />
                        <span className="text-sm font-medium">{type.label}</span>
                      </button>
                    ))}
                  </div>
                  
                  <div className="mt-3 p-3 bg-black/40/50 border border-white/5 rounded-lg">
                    <p className="text-white/50 text-xs">
                      <strong className="text-white">Professionnel ?</strong> Contactez-nous pour un devis personnalisé adapté à vos locaux.
                    </p>
                  </div>
                </div>

                {/* Step 3: Motion Detector Type */}
                <div>
                  <div className="flex items-center gap-2 mb-4">
                    <div className="w-7 h-7 rounded-full bg-emerald-500 flex items-center justify-center text-white text-sm font-bold">3</div>
                    <Label className="text-white font-semibold">Détecteurs de mouvement</Label>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-3">
                    <button
                      onClick={() => setMotionType('basic')}
                      className={`p-4 rounded-xl border transition-all text-left ${
                        motionType === 'basic'
                          ? 'bg-emerald-500/10 border-fgs-primary'
                          : 'bg-black/40 border-white/5 hover:border-fgs-primary/50'
                      }`}
                    >
                      <div className="flex justify-between items-start mb-2">
                        <span className="text-white font-medium text-sm">MotionProtect</span>
                        <span className="text-emerald-400 font-bold text-sm">{pricesTTC.motionBasic.toFixed(2)}€</span>
                      </div>
                      <p className="text-white/50 text-xs">Détection classique</p>
                    </button>
                    
                    <button
                      onClick={() => setMotionType('cam')}
                      className={`p-4 rounded-xl border transition-all text-left ${
                        motionType === 'cam'
                          ? 'bg-purple-500/20 border-purple-500'
                          : 'bg-black/40 border-white/5 hover:border-purple-500/50'
                      }`}
                    >
                      <div className="flex justify-between items-start mb-2">
                        <div className="flex items-center gap-1">
                          <span className="text-white font-medium text-sm">MotionCam</span>
                          <span className="px-1 py-0.5 bg-purple-500/30 text-purple-300 text-[9px] font-bold rounded">HDR</span>
                        </div>
                        <span className="text-purple-400 font-bold text-sm">{pricesTTC.motionCam.toFixed(2)}€</span>
                      </div>
                      <p className="text-white/50 text-xs">Avec photo vérification</p>
                    </button>
                  </div>
                </div>

                {/* Step 4: Equipment */}
                <div>
                  <div className="flex items-center gap-2 mb-4">
                    <div className="w-7 h-7 rounded-full bg-emerald-500 flex items-center justify-center text-white text-sm font-bold">4</div>
                    <Label className="text-white font-semibold">Équipements</Label>
                  </div>
                  
                  <div className="space-y-2">
                    {equipmentList.map((item) => (
                      <div 
                        key={item.id}
                        className={`flex items-center justify-between p-3 rounded-xl border ${
                          item.required ? 'bg-emerald-500/5 border-emerald-500/20' : 'bg-black/40 border-white/5'
                        }`}
                      >
                        <div className="flex items-center gap-2">
                          <item.icon className={`w-4 h-4 ${item.required ? 'text-emerald-400' : 'text-white/50'}`} />
                          <span className="text-white text-sm">{item.label}</span>
                          {item.required && <span className="text-emerald-400 text-xs">(requis)</span>}
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-white/50 text-xs">
                            {item.id === 'motionDetectors' ? getMotionPrice().toFixed(2) : pricesTTC[item.id].toFixed(2)}€
                          </span>
                          <div className="flex items-center bg-black/40 rounded-lg border border-white/5">
                            <button
                              onClick={() => updateQuantity(item.id, -1)}
                              disabled={equipment[item.id] <= item.min}
                              className="p-1.5 text-white/50 hover:text-white disabled:opacity-30"
                            >
                              <Minus className="w-3 h-3" />
                            </button>
                            <span className="w-6 text-center text-white text-sm font-semibold">
                              {equipment[item.id]}
                            </span>
                            <button
                              onClick={() => updateQuantity(item.id, 1)}
                              disabled={equipment[item.id] >= item.max}
                              className="p-1.5 text-white/50 hover:text-white disabled:opacity-30"
                            >
                              <Plus className="w-3 h-3" />
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}

                    {/* Cameras on request */}
                    <div className="p-3 rounded-xl border border-dashed border-white/5 bg-black/40/50">
                      <label className="flex items-center gap-3 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={options.wantsCameras}
                          onChange={(e) => setOptions(prev => ({ ...prev, wantsCameras: e.target.checked }))}
                          className="w-4 h-4 rounded border-white/5 bg-black/40 text-emerald-400"
                        />
                        <Camera className="w-4 h-4 text-white/50" />
                        <div>
                          <span className="text-white text-sm">Caméras de vidéoprotection</span>
                          <p className="text-white/50 text-xs">Tarif sur demande selon modèle</p>
                        </div>
                      </label>
                    </div>
                  </div>
                </div>

                {/* Step 5: Services */}
                <div>
                  <div className="flex items-center gap-2 mb-4">
                    <div className="w-7 h-7 rounded-full bg-emerald-500 flex items-center justify-center text-white text-sm font-bold">5</div>
                    <Label className="text-white font-semibold">Services</Label>
                  </div>
                  
                  <div className="space-y-3">
                    {/* Installation */}
                    <label className={`flex items-center justify-between p-4 rounded-xl border cursor-pointer ${
                      options.installation ? 'bg-emerald-500/10 border-fgs-primary' : 'bg-black/40 border-white/5'
                    }`}>
                      <div className="flex items-center gap-3">
                        <input
                          type="checkbox"
                          checked={options.installation}
                          onChange={(e) => {
                            setOptions(prev => ({ ...prev, installation: e.target.checked, programming: false }));
                            if (!e.target.checked) setConfirmed2Years(false);
                          }}
                          className="w-5 h-5 rounded border-white/5 bg-black/40 text-emerald-400"
                        />
                        <div>
                          <span className="text-white text-sm font-medium">Installation professionnelle</span>
                          <p className="text-white/50 text-xs">Pose et configuration par technicien certifié</p>
                        </div>
                      </div>
                      <span className="text-emerald-400 font-bold">{cityResult.installation_price}€ TTC</span>
                    </label>

                    {/* TVA 10% option (only if installation selected) */}
                    {options.installation && (
                      <div className={`p-4 rounded-xl border ${
                        confirmed2Years ? 'bg-emerald-500/10 border-emerald-500/50' : 'bg-black/40/50 border-white/5'
                      }`}>
                        <label className="flex items-start gap-3 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={confirmed2Years}
                            onChange={(e) => setConfirmed2Years(e.target.checked)}
                            className="w-5 h-5 mt-0.5 rounded border-white/5 bg-black/40 text-emerald-500"
                          />
                          <div>
                            <div className="flex items-center gap-2">
                              <Percent className="w-4 h-4 text-emerald-400" />
                              <span className="text-white text-sm font-medium">TVA réduite à 10%</span>
                            </div>
                            <p className="text-white/50 text-xs mt-1">
                              Je certifie que mon logement est achevé depuis plus de 2 ans
                            </p>
                            {confirmed2Years && (
                              <p className="text-emerald-400 text-xs mt-1 font-medium">
                                ✓ TVA à 10% appliquée sur votre devis
                              </p>
                            )}
                          </div>
                        </label>
                      </div>
                    )}

                    {/* Programming (only if no installation) */}
                    {!options.installation && (
                      <label className={`flex items-center justify-between p-4 rounded-xl border cursor-pointer ${
                        options.programming ? 'bg-emerald-500/10 border-fgs-primary' : 'bg-black/40 border-white/5'
                      }`}>
                        <div className="flex items-center gap-3">
                          <input
                            type="checkbox"
                            checked={options.programming}
                            onChange={(e) => setOptions(prev => ({ ...prev, programming: e.target.checked }))}
                            className="w-5 h-5 rounded border-white/5 bg-black/40 text-emerald-400"
                          />
                          <div>
                            <span className="text-white text-sm font-medium">Programmation à distance</span>
                            <p className="text-white/50 text-xs">Vous installez, on configure</p>
                          </div>
                        </div>
                        <span className="text-emerald-400 font-bold">{pricesTTC.programming}€ TTC</span>
                      </label>
                    )}

                    {/* 4G Subscription */}
                    <div className={`p-4 rounded-xl border ${
                      options.subscription4G ? 'bg-purple-500/10 border-purple-500' : 'bg-black/40 border-white/5'
                    }`}>
                      <label className="flex items-center gap-3 cursor-pointer mb-3">
                        <input
                          type="checkbox"
                          checked={options.subscription4G}
                          onChange={(e) => setOptions(prev => ({ ...prev, subscription4G: e.target.checked }))}
                          className="w-5 h-5 rounded border-white/5 bg-black/40 text-purple-500"
                        />
                        <div>
                          <span className="text-white text-sm font-medium">Abonnement 4G</span>
                          <p className="text-white/50 text-xs">Connexion secours si coupure internet</p>
                          <p className="text-white/50 text-[10px] italic">TVA 20% (service télécom)</p>
                        </div>
                      </label>
                      
                      {options.subscription4G && (
                        <div className="grid grid-cols-2 gap-2 mt-3 pl-8">
                          <button
                            onClick={() => setOptions(prev => ({ ...prev, subscription4GType: 'monthly' }))}
                            className={`p-3 rounded-lg border text-center transition-all ${
                              options.subscription4GType === 'monthly'
                                ? 'bg-purple-500/20 border-purple-500'
                                : 'bg-black/40 border-white/5 hover:border-purple-500/50'
                            }`}
                          >
                            <span className="text-purple-400 font-bold">{pricesTTC.subscription4GMonthly}€</span>
                            <span className="text-white/50 text-xs block">TTC/mois</span>
                          </button>
                          <button
                            onClick={() => setOptions(prev => ({ ...prev, subscription4GType: 'annual' }))}
                            className={`p-3 rounded-lg border text-center transition-all relative ${
                              options.subscription4GType === 'annual'
                                ? 'bg-purple-500/20 border-purple-500'
                                : 'bg-black/40 border-white/5 hover:border-purple-500/50'
                            }`}
                          >
                            <span className="absolute -top-2 right-2 px-1.5 py-0.5 bg-fgs-success text-white text-[9px] font-bold rounded">-7€</span>
                            <span className="text-purple-400 font-bold">{pricesTTC.subscription4GAnnual}€</span>
                            <span className="text-white/50 text-xs block">TTC/an</span>
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Summary */}
                <div className="bg-black/40 rounded-xl p-5 border border-white/5">
                  <h4 className="font-heading text-lg font-bold text-white mb-4">Récapitulatif</h4>
                  
                  <div className="space-y-2 text-sm mb-4">
                    <div className="flex justify-between">
                      <span className="text-white/50">Équipements HT</span>
                      <span className="text-white">{totals.equipmentHT.toFixed(2)}€</span>
                    </div>
                    {totals.servicesHT > 0 && (
                      <div className="flex justify-between">
                        <span className="text-white/50">
                          {options.installation ? 'Installation HT' : 'Programmation HT'}
                        </span>
                        <span className="text-white">{totals.servicesHT.toFixed(2)}€</span>
                      </div>
                    )}
                    <div className="flex justify-between">
                      <span className="text-white/50">Total HT</span>
                      <span className="text-white font-medium">{totals.totalHT.toFixed(2)}€</span>
                    </div>
                    <div className="flex justify-between">
                      <span className={`${confirmed2Years ? 'text-emerald-400' : 'text-white/50'}`}>
                        TVA {tvaLabel}
                      </span>
                      <span className={`${confirmed2Years ? 'text-emerald-400' : 'text-white'}`}>
                        {totals.tva.toFixed(2)}€
                      </span>
                    </div>
                    {options.wantsCameras && (
                      <div className="flex justify-between">
                        <span className="text-white/50">Caméras</span>
                        <span className="text-purple-400 italic">Sur devis</span>
                      </div>
                    )}
                  </div>
                  
                  <div className="border-t border-white/5 pt-4 mb-4">
                    <div className="flex justify-between items-baseline">
                      <span className="text-white font-semibold">Total TTC</span>
                      <div className="text-right">
                        <span className="text-2xl font-bold text-emerald-400">{totals.totalTTC.toFixed(2)}€</span>
                        {options.wantsCameras && <span className="text-white/50 text-xs block">+ caméras</span>}
                      </div>
                    </div>
                    {totals.monthlyTTC > 0 && (
                      <p className="text-purple-400 text-sm text-right mt-1">+ {totals.monthlyTTC.toFixed(2)}€ TTC/mois (4G)</p>
                    )}
                    {confirmed2Years && (
                      <p className="text-emerald-400 text-xs text-right mt-2">
                        ✓ Vous économisez {((totals.totalHT * 0.20) - (totals.totalHT * 0.10)).toFixed(2)}€ grâce à la TVA réduite
                      </p>
                    )}
                  </div>

                  <div className="flex items-start gap-2 p-3 bg-emerald-500/10 rounded-lg mb-4">
                    <Info className="w-4 h-4 text-emerald-400 flex-shrink-0 mt-0.5" />
                    <p className="text-xs text-white/50">
                      Estimation indicative. Le devis final sera établi après étude de votre projet.
                    </p>
                  </div>

                  <Button 
                    onClick={handleRequestQuote}
                    className="w-full bg-emerald-500 hover:bg-emerald-500-hover h-12 font-semibold"
                  >
                    Recevoir mon devis
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </div>
              </>
            ) : (
              /* City not available */
              <div className="text-center py-8">
                <AlertCircle className="w-12 h-12 text-orange-400 mx-auto mb-4" />
                <h4 className="text-white font-semibold mb-2">Zone non desservie</h4>
                <p className="text-white/50 text-sm mb-4">
                  {cityResult.city} n'est pas encore dans notre zone d'intervention.<br />
                  Contactez-nous pour étudier votre projet.
                </p>
                <Button 
                  variant="outline"
                  onClick={() => document.getElementById('contact-form')?.scrollIntoView({ behavior: 'smooth' })}
                  className="border-fgs-primary text-emerald-400 hover:bg-emerald-500/10"
                >
                  Nous contacter
                </Button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default QuoteCalculator;
